import { createLogic } from "redux-logic";
import type { SetFinanceQuoteId, StartFetchType } from "../actions/UscFinanceActions";
import { setFinanceInfo, USC_FINANCE_SET_QUOTE_ID, USC_FINANCE_START_FETCH } from "../actions/UscFinanceActions";
import type {
    StockFinanceApiReturnType,
    UscFinanceQuoteSessionType,
    UsedFinanceApiReturnType,
} from "../../utils/financeUtils";
import {
    formatStockFinanceApiResponse,
    formatUsedFinanceApiResponse,
    getFinanceInsurancePayloadSelector,
} from "../../utils/financeUtils";
import Debug from "../../../../common-deprecated/Debug";
import { invalidateCode } from "../actions/UscSaveActions";
import { getSessionStorageParams, setFinanceQuoteToStorage } from "../../utils/sessionStorage";
import { UscContext } from "../../../shared-logic/types/UscCommonTypes";
import { UscFinanceDataSource } from "../../utils/constants";
import type { UscLogicType } from "../store";
import { isRetailer } from "../../../../common-deprecated/utils";

const financeFetchLogic = (createLogic as UscLogicType<StartFetchType>)({
    type: USC_FINANCE_START_FETCH,
    // eslint-disable-next-line consistent-return
    async process({ getState }, dispatch, done) {
        const currentState = getState();
        const { uscSettings, finance, vehicle, commonSettings } = currentState;
        const { monthlyPaymentUrl } = uscSettings;

        const payload = getFinanceInsurancePayloadSelector("finance")(currentState);

        const vehicleForSaleId = vehicle.result?.id;

        if (!payload || !vehicleForSaleId) return done();

        try {
            // The finance quote can come from multiple sources, but we can assume the one in the finance store is the correct one.
            // The finance init hook should take care of making sure the correct quote is set in finance state.
            const { financeQuoteId } = finance;

            const result = await fetch(monthlyPaymentUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const parsedResult = await result.json();

            const { financeInfo, monthlyPrice, services, accessories, costs } =
                vehicle.type === UscContext.Used
                    ? formatUsedFinanceApiResponse(parsedResult as UsedFinanceApiReturnType)
                    : formatStockFinanceApiResponse(parsedResult as StockFinanceApiReturnType);

            dispatch(
                setFinanceInfo(
                    financeInfo,
                    monthlyPrice,
                    payload,
                    {
                        source:
                            vehicle.type === UscContext.Used
                                ? UscFinanceDataSource.ApiUsed
                                : UscFinanceDataSource.ApiStock,
                        data: parsedResult,
                    },
                    financeQuoteId || "",
                    accessories,
                    services,
                    costs,
                ),
            );

            // Invalidate the tyCode as this includes finance and should be refetched
            // This is not necessary for retailer as no quoteId has te be propagated to ccSave. (calculators are disabled)
            // This would else result in an unnecessary rerender.
            if (!isRetailer(commonSettings)) dispatch(invalidateCode());
        } catch (ex) {
            Debug.error("Failed to fetch finance quote", ex);
        }
        done();
    },
});

const quoteIdLogic = (createLogic as UscLogicType<SetFinanceQuoteId>)({
    type: USC_FINANCE_SET_QUOTE_ID,
    process({ action, getState }, _, done) {
        const { commonSettings, vehicle } = getState();
        const sessionStorageParams = getSessionStorageParams({ commonSettings, vehicle });
        const vehicleForSaleId = vehicle.result?.id;
        if (vehicleForSaleId) {
            const savedQuote: UscFinanceQuoteSessionType = { financeQuoteId: action.financeQuoteId };
            setFinanceQuoteToStorage(sessionStorageParams, savedQuote, vehicleForSaleId);
        }

        done();
    },
});

export default [financeFetchLogic, quoteIdLogic];
